// @catalog F.006
/**
 * DashboardVentas — orchestrator de /ventas
 * (plan dashboard_executive_redesign 2026-05-21).
 *
 * Capa presentacional refrescada al target visual del handoff:
 *   - PageHead + PeriodSelector + botón "Filtrar" (deltas fijos vs. año anterior)
 *   - AlertBanner condicional para tiendas con caída YoY > 10%
 *   - KPI strip ejecutivo (4 cards con sparklines + ProjectionBar)
 *   - SubnavTabs (Resumen / Tiendas POS / Vendedores / Productos)
 *   - DrillPanel lateral (StoreDrillPanel)
 *   - FilterBar relegado a popover (toggle "Filtrar")
 *
 * Vistas y helpers extraídos a ./Ventas/ para respetar P7 (≤500 líneas/archivo):
 *   - ResumenView (grid SalesTimeline + Composición + rankings + top productos)
 *   - VentasSecondaryViews (Tiendas POS / Vendedores SO / Productos)
 *   - StoreDrillPanel
 *   - dashboard-format (etiquetas de mes, defaultPeriod, subtítulo, rankings)
 *
 * Capa de datos:
 *   - useDashboardVentasMetrics
 *   - period + filters en useState (NO se persisten): /ventas arranca SIEMPRE en
 *     el mes en curso por default (ver defaultPeriod en ./Ventas/dashboard-format).
 *   - AnalyticsService.formatCurrency (MXN)
 */
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Collapse,
  Skeleton,
  Snackbar,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useDashboardVentasMetrics } from "../hooks/useAnalytics";
import { useLocalStorageState } from "../hooks/useLocalStorageState";
import { useProductDetailDialog } from "../hooks/useProductDetailDialog";
import { ProductDetailDrawer } from "./Forecast/product-detail/ProductDetailDrawer";
import { type StoreMetrics } from "../services/analytics.service";
import {
  FilterBar,
  type DateRange,
  type StoreOption,
} from "./Dashboard/FilterBar";
import { KPICardsVentas } from "./Ventas/KPICards";
import {
  monthNameToIndex,
  rerankStores,
  splitStoresByChannel,
} from "./Ventas/helpers";
import {
  MONTH_LABELS_FULL,
  buildVentasSubtitle,
  clampToCurrentMonth,
  defaultPeriod,
  formatMonthRange,
  isoToMonthKey,
  toRankingItems,
} from "./Ventas/dashboard-format";
import { ResumenView } from "./Ventas/ResumenView";
import {
  ProductosView,
  TiendasView,
  VendedoresView,
} from "./Ventas/VentasSecondaryViews";
import { StoreDrillPanel } from "./Ventas/StoreDrillPanel";
import { PageHead } from "./analytics/PageHead";
import {
  PeriodSelector,
  type PeriodKey,
  type PeriodValue,
  periodToDateRange,
} from "./analytics/PeriodSelector";
import { AlertBanner } from "./analytics/AlertBanner";
import { SubnavTabs } from "./analytics/SubnavTabs";

type VentasView = "resumen" | "tiendas" | "vendedores" | "productos";

interface FiltersState {
  storeName: string | null;
  dateRange: DateRange;
}

export default function DashboardVentas(): JSX.Element {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Switch de accent CSS — /ventas usa azul.
  useEffect(() => {
    const previous = document.documentElement.dataset["accent"];
    document.documentElement.dataset["accent"] = "blue";
    return () => {
      if (previous) document.documentElement.dataset["accent"] = previous;
      else delete document.documentElement.dataset["accent"];
    };
  }, []);

  // period NO se persiste: /ventas arranca SIEMPRE en el mes en curso
  // (defaultPeriod="mtd") en cada montaje del módulo. El cambio de período vive
  // solo durante la visita (sobrevive el switch de tabs internas, que no remonta
  // el componente; no sobrevive salir y volver a /ventas).
  // F1: period puede ser un preset o "custom" (rango manual aplicado). Fuente de
  // verdad junto con filters.dateRange — los handlers los mantienen sincronizados.
  const [period, setPeriod] = useState<PeriodValue>(defaultPeriod());
  // view sí se persiste (preferencia de pestaña del subnav); no afecta el período.
  const [view, setView] = useLocalStorageState<VentasView>(
    "villapps:ventas:view",
    "resumen",
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [drillItem, setDrillItem] = useState<StoreMetrics | null>(null);

  // Dialog de detalle del producto (mismo que /compras/forecast).
  const {
    dialogState: productDialogState,
    openProductDetail,
    closeProductDetail,
    resolveError,
    clearResolveError,
  } = useProductDetailDialog();

  // Rango inicial coherente con el período por default (mes en curso). Sólo se usa
  // al inicializar filters en el primer render. period="custom" no es un preset →
  // usa el default coherente.
  const initialPeriodRange = useMemo(
    () => periodToDateRange(period === "custom" ? defaultPeriod() : period),
    [period],
  );
  // filters tampoco se persiste: el módulo arranca limpio (sin filtro de tienda)
  // en el mes en curso cada vez que se abre.
  const [filters, setFilters] = useState<FiltersState>({
    storeName: null,
    dateRange: initialPeriodRange,
  });

  // F1: la sincronización period ↔ dateRange la hacen handlers explícitos
  // (handlePeriodChange / handleDateRangeChange), NO un useEffect unidireccional
  // frágil que clobbereaba el override manual y dejaba el chip mintiendo.

  const startMonth = isoToMonthKey(filters.dateRange.start);
  const endMonth = clampToCurrentMonth(isoToMonthKey(filters.dateRange.end));

  const query = useDashboardVentasMetrics({
    startMonth,
    endMonth,
    storeName: filters.storeName,
  });

  const data = query.data;

  const storeOptions = useMemo<StoreOption[]>(() => {
    if (!data) return [];
    return (data.topStores ?? []).map((s) => ({
      id: s.storeName,
      label: s.storeName,
    }));
  }, [data]);

  const selectedStoreOption =
    storeOptions.find((o) => o.label === filters.storeName) ?? null;

  const handleStoreChange = (next: StoreOption | null): void => {
    setFilters((prev) => ({ ...prev, storeName: next?.label ?? null }));
  };

  // Clic en un chip de período: el chip manda el rango (fuente única). Los
  // DatePickers del FilterBar reflejarán ese rango vía su useEffect de resync.
  const handlePeriodChange = (next: PeriodKey): void => {
    setPeriod(next);
    setFilters((prev) => ({ ...prev, dateRange: periodToDateRange(next) }));
  };

  // Edición manual de fechas aplicada: el período pasa a "custom" → ningún chip
  // preset queda resaltado (se muestra "Personalizado"), el chip ya no miente.
  const handleDateRangeChange = (next: DateRange): void => {
    setPeriod("custom");
    setFilters((prev) => ({ ...prev, dateRange: next }));
  };

  const handleClearAll = (): void => {
    setPeriod(defaultPeriod());
    setFilters({
      storeName: null,
      dateRange: periodToDateRange(defaultPeriod()),
    });
  };

  // Los DatePickers del FilterBar muestran el rango SOLO cuando es un rango manual
  // ("Personalizado"). Con un preset activo (MTD/1M/3M/YTD/12M/24M) van vacíos y
  // abren en el año actual (referenceDate=hoy): así elegir "mayo" da mayo del año
  // en curso y NO se hereda el año de inicio del preset (12M arranca el año previo).
  // El rango del preset se comunica en el chip + subtítulo, no en los pickers.
  const filterBarDateRange: DateRange =
    period === "custom" ? filters.dateRange : { start: null, end: null };

  if (query.isLoading) {
    return (
      <Box sx={{ p: isMobile ? 2 : 3 }}>
        <Stack spacing={3}>
          <Skeleton variant="rectangular" height={64} sx={{ borderRadius: 1 }} />
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={160}
                sx={{ flex: 1, borderRadius: 1 }}
              />
            ))}
          </Stack>
          <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 1 }} />
        </Stack>
      </Box>
    );
  }

  if (query.isError) {
    return (
      <Box sx={{ p: isMobile ? 2 : 3 }}>
        <Alert severity="error">
          Error al cargar las métricas mensuales de Dashboard de Ventas.
          Verifique que el servidor backend esté ejecutándose.
        </Alert>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: isMobile ? 2 : 3 }}>
        <Alert severity="warning">
          No hay datos mensuales disponibles para el rango seleccionado.
        </Alert>
        <Stack direction="row" sx={{ mt: 2 }}>
          <Typography
            variant="body2"
            color="primary"
            sx={{ cursor: "pointer", textDecoration: "underline" }}
            onClick={handleClearAll}
          >
            Restaurar filtros por defecto
          </Typography>
        </Stack>
      </Box>
    );
  }

  // Separar POS / vendedores SO.
  const allStores = (data.topStores ?? []) as StoreMetrics[];
  const { storesPOS, storesSO } = splitStoresByChannel(allStores);
  const finalPOS = rerankStores(storesPOS);
  const finalSO = rerankStores(storesSO);
  const totalPOS = finalPOS.reduce((s, x) => s + x.totalSales, 0);
  const totalSO = finalSO.reduce((s, x) => s + x.totalSales, 0);
  const displayProducts = data.topProductsCurrentMonth ?? [];

  // Current month info para subtitle dinámico.
  const sortedMonths = [...(data.monthlyData ?? [])].sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    return monthNameToIndex(a.month) - monthNameToIndex(b.month);
  });
  const current = sortedMonths[sortedMonths.length - 1] ?? null;
  const currentLabel = current
    ? `${MONTH_LABELS_FULL[monthNameToIndex(current.month)] ?? current.month} ${current.year}`
    : "Sin datos";

  // F5: descriptor del período que reflejan banner/secciones (sigue el rango
  // filtrado, NO "mes actual"). Un mes → "Mayo 2026"; varios → "ene–may 2026".
  const firstMonth = sortedMonths[0];
  const periodDescriptor =
    current && sortedMonths.length > 1 && firstMonth
      ? formatMonthRange(
          monthNameToIndex(firstMonth.month),
          firstMonth.year,
          monthNameToIndex(current.month),
          current.year,
        )
      : currentLabel;

  // Underperformers para el banner.
  const underperformers: StoreMetrics[] = allStores.filter(
    (s) =>
      s.growthPercentage !== undefined &&
      s.growthPercentage !== null &&
      s.growthPercentage < -10,
  );

  // RankingBars data — top 5 por canal en resumen.
  const posRankingItems = toRankingItems(finalPOS, {
    threshold: -10,
    tagLabel: "Atención",
  });
  const voRankingItems = toRankingItems(finalSO, {
    threshold: -15,
    tagLabel: "Bajo perf.",
  });

  // Subtitle dinámico (F3): refleja el período seleccionado y su agregado.
  const subtitle = buildVentasSubtitle({
    current,
    sortedMonths,
    currentLabel,
    period,
    summaryLast3Months: data.summary
      ? data.summary.totalSalesLast3Months
      : null,
  });

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      <Stack spacing={3}>
        <PageHead
          title="Ventas"
          subtitle={subtitle}
          actions={
            <>
              <PeriodSelector value={period} onChange={handlePeriodChange} />
              <Box
                component="button"
                type="button"
                onClick={() => setFiltersOpen((v) => !v)}
                aria-expanded={filtersOpen}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "6px 10px",
                  borderRadius: "6px",
                  border: "1px solid var(--border)",
                  backgroundColor: filtersOpen
                    ? "var(--bg-2)"
                    : "var(--surface)",
                  color: "var(--ink-2)",
                  fontSize: "13px",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  "&:hover": { backgroundColor: "var(--bg-2)" },
                }}
              >
                <FilterListIcon sx={{ fontSize: 14 }} />
                Filtrar
              </Box>
            </>
          }
        />

        <Collapse in={filtersOpen} unmountOnExit>
          <FilterBar
            stores={storeOptions}
            selectedStore={selectedStoreOption}
            dateRange={filterBarDateRange}
            onStoreChange={handleStoreChange}
            onDateRangeChange={handleDateRangeChange}
            onClear={handleClearAll}
            granularity="month"
            loading={query.isFetching}
          />
        </Collapse>

        {underperformers.length > 0 && (
          <AlertBanner
            severity="neg"
            actionLabel="Ver detalle"
            onAction={() => setView("tiendas")}
          >
            <Box component="strong">
              {underperformers.length}{" "}
              {underperformers.length === 1
                ? "punto de venta"
                : "puntos de venta"}
            </Box>{" "}
            con caída mayor a 10% vs. año anterior en {periodDescriptor}:{" "}
            {underperformers
              .slice(0, 4)
              .map((u) => u.storeName)
              .join(", ")}
            {underperformers.length > 4
              ? `, +${underperformers.length - 4}`
              : ""}
            .
          </AlertBanner>
        )}

        <KPICardsVentas
          monthlyData={data.monthlyData}
          topStores={data.topStores}
          loading={query.isFetching && !query.data}
        />

        <SubnavTabs<VentasView>
          tabs={[
            { key: "resumen", label: "Resumen" },
            { key: "tiendas", label: "Tiendas POS" },
            { key: "vendedores", label: "Vendedores" },
            { key: "productos", label: "Productos" },
          ]}
          active={view}
          onChange={setView}
        />

        {view === "resumen" && (
          <ResumenView
            monthlyData={data.monthlyData ?? []}
            currentLabel={currentLabel}
            rangeLabel={periodDescriptor}
            posRankingItems={posRankingItems}
            voRankingItems={voRankingItems}
            displayProductsCount={displayProducts.length}
            totalPOS={totalPOS}
            totalSO={totalSO}
            current={current}
            onDrillStore={setDrillItem}
            onSeeAllPOS={() => setView("tiendas")}
            onSeeAllVendors={() => setView("vendedores")}
            onSeeAllProducts={() => setView("productos")}
            topProductsSlice={displayProducts.slice(0, 6)}
            onProductClick={openProductDetail}
          />
        )}

        {view === "tiendas" && (
          <TiendasView stores={finalPOS} fetching={query.isFetching} />
        )}

        {view === "vendedores" && <VendedoresView vendors={finalSO} />}

        {view === "productos" && (
          <ProductosView
            products={displayProducts}
            periodDescriptor={periodDescriptor}
            onProductClick={openProductDetail}
          />
        )}
      </Stack>

      <StoreDrillPanel
        item={drillItem}
        currentLabel={currentLabel}
        onClose={() => setDrillItem(null)}
      />

      <ProductDetailDrawer
        open={productDialogState.open}
        unifiedProductId={productDialogState.unifiedProductId}
        productName={productDialogState.productName}
        onClose={closeProductDetail}
      />

      <Snackbar
        open={!!resolveError}
        autoHideDuration={5000}
        onClose={clearResolveError}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="warning"
          onClose={clearResolveError}
          sx={{ width: "100%" }}
        >
          {resolveError}
        </Alert>
      </Snackbar>
    </Box>
  );
}
